/* wb-avis-boutique.js — le carrousel d'avis BOUTIQUE du pied de page (06/10/2026).
   Copie de référence du dépôt parent : l'original déployé est `assets/wb-avis-boutique.js`
   du thème live (en cas de doute le thème live fait foi). Chargé en `defer` par
   `snippets/wb-avis-boutique.liquid`, seulement s'il y a des avis à montrer.

   Deux rôles :
   1. Les flèches. Sans script, la liste défile au doigt ou au clavier (scroll-snap) et les
      flèches restent masquées (`hidden`). Avec, elles passent d'un avis à l'autre, en
      boucle. Pas de défilement automatique : il demanderait un bouton pause (WCAG 2.2.2).
   2. La traduction. Les avis boutique sont en français. Sur les autres langues, ils sont
      traduits à l'approche du pied de page par le service Judge.me
      `api/review_translations`, le même que `assets/wb-product-reviews-translate.js` —
      mesuré le 06/10 sur des avis boutique : CORS ouvert, ~1,7 s pour 3 avis, et la
      traduction obtenue est ensuite stockée chez Judge.me. Une langue vide compte pour du
      français. Le pied de page étant sur toutes les pages, chaque traduction est gardée
      pour la session (`sessionStorage`) : un avis n'est demandé qu'une fois par langue.
   ⚠️ Compromis assumé (choix de Simon du 06/10, « traduire à l'affichage ») : le HTML SERVI
   hors français porte les avis en français, la traduction arrive en JS. */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-wb-avis-boutique]';
  var LIST_SELECTOR = '[data-wb-avis-liste]';
  var ITEM_SELECTOR = '[data-wb-review]';
  var READY_ATTR = 'data-wb-avis-pret';
  var STATE_ATTR = 'data-wb-translation'; // pending | done | none | failed
  var API_FALLBACK = 'https://api.judge.me';
  var DEFAULT_REVIEW_LANG = 'fr';
  var BATCH_SIZE = 10;
  var TIMEOUT_MS = 15000;
  var NEAR_MARGIN = '200% 0px';
  var CACHE_PREFIX = 'wb-avis-boutique:';

  var nextFrame = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : function (callback) { return setTimeout(callback, 16); };

  // ── 1. Les flèches ──────────────────────────────────────────────────────────────
  function initCarousel(root) {
    var list = root.querySelector(LIST_SELECTOR);
    var nav = root.querySelector('[data-wb-avis-nav]');
    var items = list ? list.querySelectorAll(ITEM_SELECTOR) : [];
    if (!nav || items.length < 2) return;
    var position = nav.querySelector('[data-wb-avis-position]');
    var dots = nav.querySelectorAll('[data-wb-avis-point]');
    var current = 0;
    var pending = false;
    var target = null;      // défilement lancé par une flèche, en cours
    var targetTimer = null;

    function show(index) {
      current = (index + items.length) % items.length;
      if (position) position.textContent = (current + 1) + ' / ' + items.length;
      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.classList.toggle('is-active', i === current);
      });
    }

    // L'avis le plus proche de la position de défilement : les cartes sont espacées
    // (`gap`), un simple `scrollLeft / clientWidth` dériverait d'un avis à l'autre.
    function nearest() {
      var base = items[0].offsetLeft;
      var best = 0;
      var bestDistance = Infinity;
      for (var i = 0; i < items.length; i++) {
        var distance = Math.abs(items[i].offsetLeft - base - list.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    }

    function release() {
      target = null;
      clearTimeout(targetTimer);
    }

    function go(index) {
      show(index);
      // La douceur du défilement vient de la feuille (`scroll-behavior`), qui la coupe
      // sous `prefers-reduced-motion`. Pendant l'animation, les positions intermédiaires
      // ne doivent pas réécrire `current` : un second clic partirait d'un avis traversé.
      target = items[current].offsetLeft - items[0].offsetLeft;
      clearTimeout(targetTimer);
      targetTimer = setTimeout(release, 1000);
      if (typeof list.scrollTo === 'function') list.scrollTo({ left: target });
      else list.scrollLeft = target;
    }

    nav.querySelector('[data-wb-avis-precedent]').addEventListener('click', function () { go(current - 1); });
    nav.querySelector('[data-wb-avis-suivant]').addEventListener('click', function () { go(current + 1); });

    list.addEventListener('scroll', function () {
      if (pending) return;
      pending = true;
      nextFrame(function () {
        pending = false;
        if (target !== null) {
          if (Math.abs(list.scrollLeft - target) < 2) release();
          return;
        }
        show(nearest());
      });
    }, { passive: true });
    list.addEventListener('scrollend', release);

    nav.hidden = false;
  }

  // ── 2. La traduction ────────────────────────────────────────────────────────────
  function baseLang(code) {
    return String(code || '').toLowerCase().split('-')[0];
  }

  function needsTranslation(li, pageLocale) {
    if (li.hasAttribute(STATE_ATTR)) return false;
    var lang = baseLang(li.getAttribute('data-wb-review-lang')) || DEFAULT_REVIEW_LANG;
    return lang !== baseLang(pageLocale);
  }

  function apiUrl(root, uuids, locale) {
    var host = ((window.jdgm || {}).API_HOST || API_FALLBACK).replace(/\/$/, '');
    var params = new URLSearchParams({
      shop_domain: root.getAttribute('data-shop-domain') || '',
      platform: (window.jdgmSettings || {}).platform || 'shopify',
      review_uuids: uuids.join(','),
      language: locale,
      page: '-1', // la valeur que le widget Judge.me envoie
    });
    return host + '/api/review_translations?' + params.toString();
  }

  function fetchTranslations(url) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;
    var options = { headers: { Accept: 'application/json' } };
    if (controller) options.signal = controller.signal;
    return fetch(url, options)
      .then(function (response) {
        if (!response.ok) throw new Error('review_translations : HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        clearTimeout(timer);
        return data || {};
      }, function (error) {
        clearTimeout(timer);
        throw error;
      });
  }

  function setLines(el, text) {
    while (el.firstChild) el.removeChild(el.firstChild);
    text.split('\n').forEach(function (line, i) {
      if (i > 0) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(line));
    });
  }

  /* Le pied de page est sur TOUTES les pages : sans cache, chaque page vue hors français
     redemanderait les mêmes avis. Une traduction obtenue (ou son absence, chaîne vide) est
     gardée pour la session ; un échec ne l'est pas, il sera retenté à la page suivante. */
  function cacheKey(locale, uuid) {
    return CACHE_PREFIX + locale + ':' + uuid;
  }

  function cacheGet(locale, uuid) {
    try {
      return window.sessionStorage.getItem(cacheKey(locale, uuid));
    } catch (e) {
      return null; // stockage refusé (navigation privée, cookies bloqués) : sans cache
    }
  }

  function cacheSet(locale, uuid, text) {
    try {
      window.sessionStorage.setItem(cacheKey(locale, uuid), text);
    } catch (e) {
      // stockage plein ou refusé : la traduction reste affichée, simplement pas gardée
    }
  }

  function translatedText(entry) {
    return entry && entry.body && entry.body.success ? String(entry.body.translated_text || '').trim() : '';
  }

  function applyText(root, li, text) {
    var body = li.querySelector('[data-wb-avis-texte]');
    // Identique à l'original : avis dont Judge.me a mal détecté la langue, rien à montrer.
    if (!body || !text || text === body.textContent.trim()) {
      li.setAttribute(STATE_ATTR, 'none');
      return;
    }
    setLines(body, text);
    var label = root.getAttribute('data-wb-traduit');
    var quote = body.closest('blockquote') || body;
    if (label && quote.parentNode) {
      var note = document.createElement('p');
      note.className = 'wb-avis-boutique__traduit';
      note.textContent = label;
      quote.parentNode.insertBefore(note, quote.nextSibling);
    }
    li.setAttribute(STATE_ATTR, 'done');
  }

  // Un avis qui échoue ne fait pas échouer ses voisins du même lot.
  function applySafely(root, li, text) {
    try {
      applyText(root, li, text);
    } catch (e) {
      li.setAttribute(STATE_ATTR, 'failed');
    }
  }

  function translateBatch(root, batch, locale) {
    var uuids = batch.map(function (li) { return li.getAttribute('data-wb-review'); });
    fetchTranslations(apiUrl(root, uuids, locale))
      .then(function (data) {
        batch.forEach(function (li) {
          var uuid = li.getAttribute('data-wb-review');
          var text = translatedText(data[uuid]);
          cacheSet(locale, uuid, text);
          applySafely(root, li, text);
        });
      })
      .catch(function () {
        /* Voulu sans message, comme sur les fiches produit : l'avis reste en français.
           `failed` évite de le redemander sur cette page. */
        batch.forEach(function (li) { li.setAttribute(STATE_ATTR, 'failed'); });
      });
  }

  function pendingItems(root) {
    var locale = root.getAttribute('data-locale') || '';
    return Array.prototype.filter.call(
      root.querySelectorAll(LIST_SELECTOR + ' > ' + ITEM_SELECTOR),
      function (li) { return needsTranslation(li, locale); });
  }

  function translate(root) {
    var locale = root.getAttribute('data-locale') || '';
    var items = pendingItems(root).filter(function (li) {
      var cached = cacheGet(locale, li.getAttribute('data-wb-review'));
      if (cached === null) return true;
      applySafely(root, li, cached);
      return false;
    });
    for (var i = 0; i < items.length; i += BATCH_SIZE) {
      var batch = items.slice(i, i + BATCH_SIZE);
      batch.forEach(function (li) { li.setAttribute(STATE_ATTR, 'pending'); });
      translateBatch(root, batch, locale);
    }
  }

  function whenNear(el, run) {
    if (typeof IntersectionObserver !== 'function') {
      run();
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      run();
    }, { rootMargin: NEAR_MARGIN });
    observer.observe(el);
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), function (root) {
      if (root.hasAttribute(READY_ATTR)) return;
      root.setAttribute(READY_ATTR, '');
      initCarousel(root);
      if (typeof fetch === 'function' && pendingItems(root).length) {
        whenNear(root, function () { translate(root); });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Éditeur de thème : la section du pied de page est re-rendue sans recharger la page.
  document.addEventListener('shopify:section:load', init);
})();
